// Windows Media Player — the old-movies theater. 32 public-domain films streamed from the Internet
// Archive's own player (archive.org/embed) inside a Windows Media Player 8 window: the XP menu bar,
// the vertical feature taskbar on the left, the Now Playing list on the right, silver transport.
// We host nothing. Community ask 2026-09-08: "old movies". Restyled 2026-09-12 (lib/xp palette).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { WindowDropDowns } from 'components';
import { XP, xpCss } from 'lib/xp';
import { FILMS, FILM_GROUPS, posterOf, embedOf, pageOf, findFilm } from './films';

const menus = (showList, canFull) => ({
  File: [
    { type: 'item', text: 'Open on archive.org' },
    { type: 'item', text: 'Full Screen', disable: !canFull },
    { type: 'separator' },
    { type: 'item', text: 'Close' },
  ],
  View: [
    { type: 'item', text: 'Now Playing list', symbol: showList ? 'check' : undefined },
    { type: 'item', text: 'Full Screen', disable: !canFull },
  ],
  Play: [
    { type: 'item', text: 'Previous' },
    { type: 'item', text: 'Next' },
    { type: 'item', text: 'Shuffle' },
  ],
  Help: [{ type: 'item', text: 'About Windows Media Player' }],
});

// the WMP 8 feature taskbar, top to bottom, as shipped with XP
const FEATURES = ['Now Playing', 'Media Guide', 'Copy from CD', 'Media Library', 'Radio Tuner', 'Copy to CD or Device', 'Skin Chooser'];

function Movies({ onClose, isFocus, film: startFilm }) {
  const start = useMemo(() => {
    const f = typeof startFilm === 'string' ? findFilm(startFilm) : startFilm;
    return f ? FILMS.findIndex(x => x.id === f.id) : -1;
  }, [startFilm]);
  const [index, setIndex] = useState(start >= 0 ? start : 0);
  const [nonce, setNonce] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [showList, setShowList] = useState(window.innerWidth >= 800);
  const [about, setAbout] = useState(false);
  const listRef = useRef(null);
  const film = FILMS[index];
  const frameId = `ic-wmp-${film.id}-${nonce}`;

  const grouped = useMemo(() => FILM_GROUPS.map(g => ({ ...g, items: FILMS.map((f, i) => ({ ...f, i })).filter(f => f.group === g.id) })), []);

  function select(i) {
    const n = ((i % FILMS.length) + FILMS.length) % FILMS.length;
    if (n === index) return;
    setIndex(n);
    setLoaded(false);
  }
  const next = () => select(index + 1);
  const prev = () => select(index - 1);
  const shuffle = () => select(Math.floor(Math.random() * FILMS.length));

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector('[data-on="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [index, showList]);

  useEffect(() => {
    if (!isFocus) return undefined;
    function onKey(e) {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') prev();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function fullScreen() {
    const f = document.getElementById(frameId);
    if (f && f.requestFullscreen) f.requestFullscreen();
  }
  function onClickOptionItem(item) {
    switch (item) {
      case 'Close':
        onClose();
        break;
      case 'Open on archive.org':
        window.open(pageOf(film), '_blank', 'noopener');
        break;
      case 'Full Screen':
        fullScreen();
        break;
      case 'Now Playing list':
        setShowList(s => !s);
        break;
      case 'Previous':
        prev();
        break;
      case 'Next':
        next();
        break;
      case 'Shuffle':
        shuffle();
        break;
      case 'About Windows Media Player':
        setAbout(a => !a);
        break;
      default:
    }
  }
  // the feature bar: two of them do something, the rest are period-correct decoration
  function onFeature(name) {
    if (name === 'Media Guide') window.open(pageOf(film), '_blank', 'noopener');
    else if (name === 'Media Library') setShowList(s => !s);
    else if (name === 'Now Playing') setShowList(true);
  }

  return (
    <Div>
      <section className="wmp__menu">
        <WindowDropDowns items={menus(showList, true)} onClickItem={onClickOptionItem} height={21} />
        <span className="wmp__menu__brand">Windows Media Player</span>
      </section>
      <div className="wmp__body">
        <aside className="wmp__features">
          {FEATURES.map(name => (
            <button
              type="button"
              key={name}
              className={`wmp__feature${name === 'Now Playing' ? ' wmp__feature--on' : ''}`}
              onClick={() => onFeature(name)}
              title={name === 'Media Guide' ? 'this film on archive.org' : name === 'Media Library' ? 'show / hide the list' : name === 'Now Playing' ? 'the film that is playing' : 'not in this version'}
            >
              {name}
            </button>
          ))}
        </aside>
        <div className="wmp__stage">
          {!loaded && (
            <div className="wmp__loading">
              <div className="wmp__loading__title">{film.title}</div>
              <div className="wmp__loading__sub">{film.year} · {film.mins} min · public domain</div>
              <div className="wmp__loading__bar"><i /></div>
              <div className="wmp__loading__hint">buffering from the Internet Archive… press play when the poster appears</div>
            </div>
          )}
          <iframe
            id={frameId}
            key={`${film.id}-${nonce}`}
            title={film.title}
            src={embedOf(film)}
            allow="fullscreen; autoplay; picture-in-picture"
            onLoad={() => setLoaded(true)}
            className={loaded ? 'wmp__frame wmp__frame--on' : 'wmp__frame'}
          />
          {about && (
            <div className="wmp__about" onClick={() => setAbout(false)}>
              <div className="wmp__about__box" onClick={e => e.stopPropagation()}>
                <b>Windows Media Player — old movies</b>
                <p>{FILMS.length} films that belong to everyone: public domain, streamed straight from the Internet Archive&apos;s player. Nothing is hosted here.</p>
                <p>Horror, sci-fi, noir, screwball, silent, cult. Arrow keys skip. Play menu shuffles.</p>
                <p>the internet has a movie night now. (real)</p>
                <span className="wmp__about__ok" onClick={() => setAbout(false)}>OK</span>
              </div>
            </div>
          )}
        </div>
        {showList && (
          <aside className="wmp__list" ref={listRef}>
            <div className="wmp__list__head">
              <span>Now Playing</span>
              <em>{FILMS.length} films</em>
              <span className="wmp__list__x" onClick={() => setShowList(false)} title="hide list">×</span>
            </div>
            {grouped.map(g => (
              <div key={g.id}>
                <div className="wmp__list__group">{g.label}</div>
                {g.items.map(f => (
                  <div
                    key={f.id}
                    className={`wmp__item${f.i === index ? ' wmp__item--on' : ''}`}
                    data-on={f.i === index ? 'true' : 'false'}
                    onClick={() => select(f.i)}
                    onDoubleClick={() => { select(f.i); setNonce(n => n + 1); }}
                    title={f.blurb}
                  >
                    <img src={posterOf(f)} alt="" loading="lazy" />
                    <div className="wmp__item__meta">
                      <div className="wmp__item__title">{f.title}</div>
                      <div className="wmp__item__sub">{f.year} · {f.mins} min</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </aside>
        )}
      </div>
      <div className="wmp__transport">
        <div className="wmp__seek"><i /></div>
        <div className="wmp__transport__row">
          <div className="wmp__btns">
            <button type="button" onClick={prev} title="Previous">⏮</button>
            <button type="button" className="wmp__play" onClick={() => setNonce(n => n + 1)} title="Reload the player">▶</button>
            <button type="button" onClick={next} title="Next">⏭</button>
            <button type="button" onClick={shuffle} title="Shuffle">⤮</button>
            {!showList && <button type="button" onClick={() => setShowList(true)} title="Show list">☰</button>}
          </div>
          <div className="wmp__now">
            <div className="wmp__now__title">{film.title} <span>({film.year})</span></div>
            <div className="wmp__now__blurb">{film.blurb}</div>
          </div>
          <div className="wmp__badge">public domain · Internet Archive</div>
        </div>
      </div>
    </Div>
  );
}

// WMP 8 chrome: XP menus on beige, then the player itself in the "Windows XP" skin — silver-blue
// plastic, navy feature bar, black stage, white Now Playing list, round silver transport buttons.
const Div = styled.div`
  ${xpCss};
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #cfd8ea;
  .wmp__menu { height: 22px; flex-shrink: 0; display: flex; align-items: center; background: ${XP.face}; border-bottom: 1px solid ${XP.shadow}; padding-left: 2px; }
  .wmp__menu__brand { margin-left: auto; padding-right: 8px; color: ${XP.inkDim}; font-style: italic; }
  .wmp__body { flex: 1; min-height: 0; display: flex; background: linear-gradient(180deg, #dfe6f3, #b9c6df); padding: 6px; gap: 6px; }
  .wmp__features { width: 92px; flex-shrink: 0; display: flex; flex-direction: column; gap: 3px; padding: 4px 3px; border-radius: 6px; background: linear-gradient(180deg, #3d5a99 0%, #1f3d78 100%); box-shadow: inset 0 1px 0 rgba(255,255,255,.35), 0 1px 2px rgba(0,0,0,.3); }
  .wmp__feature { font: 10px Tahoma, sans-serif; color: #eef3ff; text-align: left; padding: 6px 6px; border: 1px solid transparent; border-radius: 4px; background: none; cursor: pointer; line-height: 1.15;
    &:hover { border-color: #8fb1ff; background: rgba(255,255,255,.12); }
    &--on { background: linear-gradient(180deg, #f3f7ff, #c9d8f6); color: #0f2d73; font-weight: 700; border-color: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.35); } }
  .wmp__stage { flex: 1; min-width: 0; position: relative; background: #000; border: 3px solid #5a6f9e; border-radius: 8px; overflow: hidden; box-shadow: inset 0 0 0 1px #1c2d55, 0 1px 3px rgba(0,0,0,.4); }
  .wmp__frame { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; opacity: 0; transition: opacity .4s; }
  .wmp__frame--on { opacity: 1; }
  .wmp__loading { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; text-align: center; padding: 20px; pointer-events: none; }
  .wmp__loading__title { font-size: 20px; font-weight: 700; color: #fff; }
  .wmp__loading__sub { color: #b9c6df; }
  .wmp__loading__bar { width: 220px; height: 14px; border: 1px solid #8fa3c9; background: #101a2e; padding: 2px;
    i { display: block; height: 100%; width: 40%; background: repeating-linear-gradient(90deg, #4f8ef7 0 8px, transparent 8px 11px); animation: wmp-slide 1.2s linear infinite; } }
  @keyframes wmp-slide { from { transform: translateX(-40%); } to { transform: translateX(250%); } }
  .wmp__loading__hint { color: #8fa3c9; max-width: 340px; }
  .wmp__list { width: 236px; flex-shrink: 0; overflow-y: auto; background: #fff; border: 1px solid #7f9db9; border-radius: 4px; }
  .wmp__list__head { position: sticky; top: 0; z-index: 1; display: flex; align-items: center; gap: 6px; padding: 5px 8px; background: linear-gradient(180deg, #eef3ff, #c9d8f6); border-bottom: 1px solid #7f9db9; color: #0f2d73; font-weight: 700;
    em { font-style: normal; font-weight: 400; color: #3d5a99; flex: 1; } }
  .wmp__list__x { cursor: pointer; color: #3d5a99; padding: 0 4px; &:hover { color: ${XP.red}; } }
  .wmp__list__group { padding: 8px 8px 3px; color: #3d5a99; font-size: 10px; letter-spacing: 1px; text-transform: uppercase; }
  .wmp__item { display: flex; gap: 8px; align-items: center; padding: 4px 8px; cursor: default; border-left: 3px solid transparent;
    img { width: 34px; height: 46px; object-fit: cover; background: #000; border: 1px solid ${XP.shadow}; flex-shrink: 0; }
    &:hover { background: #eef4ff; }
    &--on { background: ${XP.selection}; border-left-color: #0f2d73; .wmp__item__title, .wmp__item__sub { color: #fff; } } }
  .wmp__item__meta { min-width: 0; }
  .wmp__item__title { color: ${XP.ink}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .wmp__item__sub { color: ${XP.inkDim}; font-size: 10px; }
  .wmp__transport { flex-shrink: 0; padding: 4px 10px 6px; background: linear-gradient(180deg, #e9eef8 0%, #c2cee4 60%, #a9b8d6 100%); border-top: 1px solid #fff; box-shadow: inset 0 -1px 0 #7f9db9; }
  .wmp__seek { height: 8px; margin: 2px 2px 6px; border: 1px solid #7f9db9; border-radius: 4px; background: #fff; overflow: hidden; i { display: block; height: 100%; width: 18%; background: linear-gradient(180deg, #7fb2ff, #1f5fd0); } }
  .wmp__transport__row { display: flex; align-items: center; gap: 12px; }
  .wmp__btns { display: flex; gap: 5px; align-items: center; }
  .wmp__btns button { width: 30px; height: 26px; border-radius: 50%; border: 1px solid #5a6f9e; background: radial-gradient(circle at 40% 35%, #ffffff, #bcc8dd 70%, #8fa3c9); color: #0f2d73; font-size: 12px; cursor: pointer; line-height: 1; padding: 0; box-shadow: 0 1px 2px rgba(0,0,0,.35);
    &:hover { background: radial-gradient(circle at 40% 35%, #ffffff, #d7e2f7 70%, #a9bde0); } }
  .wmp__btns .wmp__play { width: 40px; height: 40px; font-size: 16px; color: #fff; border-color: #0f2d73; background: radial-gradient(circle at 40% 35%, #8fb9ff, #1f5fd0 70%, #0f2d73); box-shadow: 0 2px 4px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.5);
    &:hover { background: radial-gradient(circle at 40% 35%, #a9c9ff, #2f6fe0 70%, #123a8a); } }
  .wmp__now { flex: 1; min-width: 0; }
  .wmp__now__title { color: #0f2d73; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; span { color: #3d5a99; font-weight: 400; } }
  .wmp__now__blurb { color: #3d5a99; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .wmp__badge { color: #3d5a99; font-size: 10px; letter-spacing: .5px; white-space: nowrap; }
  .wmp__about { position: absolute; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 2; }
  .wmp__about__box { background: ${XP.face}; border: 1px solid #0a246a; box-shadow: 4px 4px 0 rgba(0,0,0,.4); padding: 14px 18px; width: 330px; color: #000; p { margin: 6px 0; } }
  .wmp__about__ok { display: inline-block; margin-top: 6px; padding: 2px 18px; border: 1px solid #003c74; border-radius: 3px; background: linear-gradient(#fff,#ecebe5); cursor: default; }
  @media (max-width: 800px) {
    .wmp__features { display: none; }
    .wmp__list { position: absolute; inset: 28px 6px 70px 6px; width: auto; z-index: 2; }
    .wmp__badge { display: none; }
  }
`;

export default Movies;
